import {
    formatOptionalIntegerInput,
    formatOptionalNumberInput,
    parseOptionalCurrency,
    parseOptionalInteger,
} from '@/components/retailChain/validators/retailChainValidators.js';

const DISCOUNT_LEVELS = [1, 2, 3, 4, 5, 6];

const configurationFields = [
    'forwarderType',
    'operators',
    'couponSource',
    'depositWithdrawalMethods',
    'serviceCompanyIds',
    'defaultServiceCompanyId',
    'defaultDepositValue',
    'defaultDepositPoints',
    ...DISCOUNT_LEVELS.flatMap(level => [`discountLimitL${level}`, `discountValueL${level}`]),
];

const payloadFields = [
    'name',
    'retailChainId',
    'forwarderType',
    'operators',
    'couponSource',
    'depositWithdrawalMethods',
    'serviceCompanyIds',
    'defaultServiceCompanyId',
    'description',
    'defaultDepositValue',
    'defaultDepositPoints',
    'logo',
    ...DISCOUNT_LEVELS.flatMap(level => [`discountLimitL${level}`, `discountValueL${level}`]),
];

const isFilled = value => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
};

export const brandHasConfigurationOverride = record => {
    if (!record) {
        return false;
    }

    if (typeof record.overrideConfiguration === 'boolean') {
        return record.overrideConfiguration;
    }

    return configurationFields.some(field => isFilled(record[field]));
};

export const toBrandFormValues = record => {
    if (!record) {
        return record;
    }

    const hasLogo = Boolean(record.currentLogo || (typeof record.logo === 'string' && record.logo));
    const values = {
        ...record,
        overrideConfiguration: brandHasConfigurationOverride(record),
        logo: null,
        currentLogo: Boolean(record.currentLogo) || hasLogo,
        description: record.description ?? '',
        defaultDepositValue: formatOptionalNumberInput(record.defaultDepositValue),
        defaultDepositPoints: formatOptionalIntegerInput(record.defaultDepositPoints),
    };

    DISCOUNT_LEVELS.forEach(level => {
        values[`discountLimitL${level}`] = formatOptionalIntegerInput(record[`discountLimitL${level}`]);
        values[`discountValueL${level}`] = formatOptionalIntegerInput(record[`discountValueL${level}`]);
    });

    return values;
};

export const transformBrandPayload = data => {
    const overrideConfiguration = Boolean(data?.overrideConfiguration);
    const payload = {};
    payloadFields.forEach(field => {
        if (Object.hasOwn(data, field)) {
            payload[field] = data[field];
        }
    });
    if (data.id) {
        payload.id = data.id;
    }

    const logo = payload.logo;
    payload.logo = logo && typeof logo === 'object' && logo.rawFile instanceof File ? logo : null;
    payload.description = payload.description ? payload.description : null;

    if (!overrideConfiguration) {
        configurationFields.forEach(field => {
            delete payload[field];
        });
        return payload;
    }

    payload.defaultDepositValue = parseOptionalCurrency(payload.defaultDepositValue);
    payload.defaultDepositPoints = parseOptionalInteger(payload.defaultDepositPoints);

    DISCOUNT_LEVELS.forEach(level => {
        payload[`discountLimitL${level}`] = parseOptionalInteger(payload[`discountLimitL${level}`]);
        payload[`discountValueL${level}`] = parseOptionalInteger(payload[`discountValueL${level}`]);
    });

    const methods = Array.isArray(payload.depositWithdrawalMethods) ? payload.depositWithdrawalMethods : [];
    payload.depositWithdrawalMethods = methods.includes('cash') ? methods : ['cash', ...methods];

    return payload;
};
